import React, { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, mediaSrcById } from "../api";
import { useApp } from "../context/AppContext";

/** Fullscreen flyer sent by the MASTER (broadcast). Shows pending flyers on
 *  app open/resume and when a broadcast push is tapped; X closes and marks
 *  it seen (each broadcast appears at most `times` times per user). */
export const BroadcastViewer = () => {
  const { user } = useApp();
  const [queue, setQueue] = useState([]);
  const current = queue[0] || null;

  const loadPending = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/broadcasts/pending", { params: { user_id: user.id } });
      setQueue((q) => {
        const ids = new Set(q.map((b) => b.id));
        return [...q, ...(data.broadcasts || []).filter((b) => !ids.has(b.id))];
      });
    } catch (e) { /* ignore */ }
  }, [user?.id]);

  useEffect(() => {
    loadPending();
    try {
      const pending = localStorage.getItem("osc_open_broadcast");
      if (pending) { localStorage.removeItem("osc_open_broadcast"); setTimeout(() => window.dispatchEvent(new CustomEvent("osc-open-broadcast", { detail: { id: pending } })), 400); }
    } catch (e) { /* ignore */ }
    const onVis = () => { if (document.visibilityState === "visible") loadPending(); };
    const onOpen = async (e) => {
      const id = e.detail?.id;
      if (!id || !user?.id) return;
      try {
        const { data } = await api.get(`/broadcasts/${id}`, { params: { user_id: user.id } });
        setQueue((q) => [data, ...q.filter((b) => b.id !== id)]);
      } catch (err) { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("osc-open-broadcast", onOpen);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("osc-open-broadcast", onOpen); };
  }, [loadPending, user?.id]);

  const close = async () => {
    if (!current) return;
    const id = current.id;
    setQueue((q) => q.filter((b) => b.id !== id));
    try { await api.post(`/broadcasts/${id}/seen`, { user_id: user.id }); } catch (e) { /* ignore */ }
  };

  if (!current) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/90 flex flex-col" data-testid="broadcast-viewer">
      <div className="flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),12px)] pb-2 text-white">
        <div className="min-w-0">
          {current.title && <div className="font-semibold truncate" data-testid="broadcast-title">{current.title}</div>}
          {current.body && <div className="text-xs text-white/80 line-clamp-2">{current.body}</div>}
        </div>
        <button type="button" onClick={close} aria-label="close"
          className="ml-3 shrink-0 h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="broadcast-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-3 pb-[max(env(safe-area-inset-bottom),12px)]" onClick={close}>
        {current.media_id ? (
          <img src={mediaSrcById(current.media_id)} alt={current.title || "flyer"} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" data-testid="broadcast-image" onClick={(e) => e.stopPropagation()} />
        ) : (
          <div className="text-white text-center text-lg px-6" onClick={(e) => e.stopPropagation()}>{current.body}</div>
        )}
      </div>
    </div>
  );
};
